import { Box, Flex } from '@mantine/core'

import { isBudgetCategory } from '@/types/budgets.types'

import { DonutChart } from '@/components/@Shared/DonutChart/DonutChart'
import type { BudgetSpendSummaryProps } from '@/components/BudgetSpendSummary/BudgetSpendSummary.types'
import { ManageBudgets } from '@/components/ManageBudgets/ManageBudgets'
import { MultipleBarChart } from '@/components/MultipleBarChart/MultipleBarChart'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import { TransactionType } from '@/constants/transactions.constants'

import { category as BudgetCategory } from '@/__generated__/requests'

import { formatMoney } from '@/helpers/formatMoney.helper'
import { getSpentCategoryColor } from '@/helpers/getSpentCategoryColor.helper'
import { mapCategories } from '@/helpers/mapCategories.helper'
import { sanitizeEnum } from '@/helpers/sanitizeEnum.helper'

export function BudgetSpendSummary({
  budgets,
  month,
  readOnly,
  selectedType,
}: BudgetSpendSummaryProps) {
  if (!budgets) return null

  const totalValue =
    selectedType === TransactionType.INCOME
      ? budgets.totalIncome
      : budgets.totalSpend

  const chartData = budgets.budgets.map(budget => {
    const share = budgets.totalSpend
      ? (budget.spent / budgets.totalSpend) * 100
      : 0

    return {
      color: getSpentCategoryColor(
        isBudgetCategory(budget.category)
          ? budget.category
          : BudgetCategory.OTHER,
      ),
      description: `${formatMoney(budget.spent, 'USD')} spent • ${share.toFixed(1)}% of total`,
      label: isBudgetCategory(budget.category)
        ? mapCategories(budget.category)
        : sanitizeEnum(budget.category),
      name: budget.category,
      value: share,
    }
  })

  const percentageUsage =
    ((budgets.totalSpend ?? 0) / (budgets.totalBudget ?? 1)) * 100

  const percentageUsageChartData = [
    {
      color: 'var(--mantine-other-colors-green',
      initialValue: 0,
      label: 'Spent',
      value: percentageUsage,
    },
    {
      color: 'var(--mantine-other-colors-grey-bg)',
      initialValue: 0,
      label: 'Remaining',
      value: +(100 - percentageUsage),
    },
  ]

  if (chartData?.length === 0) {
    return null
  }

  return (
    <Box mb={32}>
      <Flex
        align="center"
        direction={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: 0,
          md: 24,
        }}
        justify="space-between"
      >
        <Title lineClamp={2} variant="h2">
          Total {selectedType === TransactionType.INCOME ? 'Income' : 'Spend'}{' '}
          in {month}
        </Title>

        <Title variant="h1">{formatMoney(totalValue ?? 0, 'USD')}</Title>
      </Flex>

      <DonutChart bigger data={chartData ?? []} withTooltip />

      <Flex align="center" gap={24} justify="space-between" mb={32}>
        <Text maw="55%" mb={0}>
          You spent <strong>{percentageUsage.toFixed(2)}%</strong> of your{' '}
          <strong>{formatMoney(budgets?.totalBudget ?? 0, 'USD')}</strong> total
          budget.
        </Text>

        {!readOnly && <ManageBudgets budgets={budgets} />}
      </Flex>

      <MultipleBarChart
        sections={percentageUsageChartData}
        showLabels={false}
      />
    </Box>
  )
}
